import React from "react";
import {
    BrowserRouter,
    Routes,
    Route,
    Navigate,
    useParams,
    useLocation,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n, { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from "@/i18n";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CookieBanner from "@/components/CookieBanner";
import ScrollToTop from "@/components/ScrollToTop";
import HomePage from "@/pages/Home";
import PrivacyPage from "@/pages/Privacy";
import CookiePage from "@/pages/Cookie";
import TermsPage from "@/pages/Terms";
import ThankYouPage from "@/pages/ThankYou";

function LangGate({ children }) {
    const { lang } = useParams();
    React.useEffect(() => {
        if (
            lang &&
            SUPPORTED_LANGUAGES.includes(lang) &&
            i18n.language !== lang
        ) {
            i18n.changeLanguage(lang);
            document.documentElement.setAttribute("lang", lang);
        }
    }, [lang]);

    if (!SUPPORTED_LANGUAGES.includes(lang)) {
        return <Navigate to={`/${DEFAULT_LANGUAGE}`} replace />;
    }
    return children;
}

function Layout({ children }) {
    const { i18n: instance } = useTranslation();
    const location = useLocation();
    React.useEffect(() => {
        document.documentElement.setAttribute("lang", instance.language);
        // Update <title> and <meta description>
        const title = instance.t("meta.title");
        const description = instance.t("meta.description");
        if (title) document.title = title;
        const meta = document.querySelector('meta[name="description"]');
        if (meta && description) meta.setAttribute("content", description);
    }, [instance.language, location.pathname, instance]);

    return (
        <>
            <Header />
            <main>{children}</main>
            <Footer />
            <CookieBanner />
        </>
    );
}

function LangRoutes() {
    return (
        <LangGate>
            <Layout>
                <Routes>
                    <Route index element={<HomePage />} />
                    <Route path="privacy-policy" element={<PrivacyPage />} />
                    <Route path="cookie-policy" element={<CookiePage />} />
                    <Route path="terms-conditions" element={<TermsPage />} />
                    <Route path="*" element={<HomePage />} />
                </Routes>
            </Layout>
        </LangGate>
    );
}

function App() {
    return (
        <BrowserRouter>
            <ScrollToTop />
            <Routes>
                <Route
                    path="/"
                    element={<Navigate to={`/${DEFAULT_LANGUAGE}`} replace />}
                />
                <Route path="/thank_you" element={<ThankYouPage />} />
                <Route path="/:lang/*" element={<LangRoutes />} />
                <Route
                    path="*"
                    element={<Navigate to={`/${DEFAULT_LANGUAGE}`} replace />}
                />
            </Routes>
        </BrowserRouter>
    );
}

export default App;
